/**
 * Observabilidade — OpenObserve RUM + Logs, enviados direto do browser.
 * No-op sem as chaves `VITE_OO_*` ou com `VITE_OO_INSTRUMENT=false` (os SDKs nem são baixados).
 * Ver docs/observabilidade-e-logging.md.
 */
import { watch } from 'vue'

import { registerSink } from '@/services/logger'
import { normalizeError, redactString, REDACTED, scrub, stripUrl } from '@/services/pii'
import settings from '@/settings/settings'
import { useAuthStore } from '@/stores/Auth'

const env = import.meta.env

const SITE = env.VITE_OO_SITE
const CLIENT_TOKEN = env.VITE_OO_CLIENT_TOKEN
const ORG = env.VITE_OO_ORG

// Liga/desliga tudo (RUM, logs e replay) sem apagar as chaves `VITE_OO_*`.
// Só `false`/`0` desligam: sem a variável, vale o comportamento de sempre (liga se houver chaves).
const INSTRUMENT = !['false', '0'].includes(String(env.VITE_OO_INSTRUMENT ?? '').toLowerCase())

// Replay grava o DOM e NÃO passa pelo `beforeSend`: só habilitar com a PII revisada (docs/observabilidade-e-logging.md, seção Privacidade).
const REPLAY_SAMPLE_RATE = Math.min(100, Math.max(0, Number(env.VITE_OO_REPLAY_SAMPLE_RATE) || 0))
const PRIVACY_LEVELS = ['allow', 'mask', 'mask-user-input']
const PRIVACY_LEVEL = PRIVACY_LEVELS.includes(env.VITE_OO_REPLAY_PRIVACY)
  ? env.VITE_OO_REPLAY_PRIVACY
  : 'mask-user-input'

// Mesma escala do `severity` dos logs do back (`levelname` do Python).
const SEVERITY = { debug: 'DEBUG', info: 'INFO', warn: 'WARNING', error: 'ERROR' }

function beforeSend(event) {
  try {
    if (event?.view?.url) event.view.url = stripUrl(event.view.url)
    if (event?.view?.referrer) event.view.referrer = stripUrl(event.view.referrer)
    if (event?.resource?.url) event.resource.url = stripUrl(event.resource.url)
    if (event?.error) {
      if (event.error.message) event.error.message = redactString(event.error.message)
      if (event.error.stack) event.error.stack = redactString(event.error.stack)
    }
  } catch {
    // beforeSend nunca pode lançar.
  }
  return true
}

export async function initObservability() {
  if (!INSTRUMENT || !SITE || !CLIENT_TOKEN || !ORG) return

  const [{ openobserveRum }, { openobserveLogs }] = await Promise.all([
    import('@openobserve/browser-rum'),
    import('@openobserve/browser-logs'),
  ])

  const common = {
    clientToken: CLIENT_TOKEN,
    site: SITE,
    organizationIdentifier: ORG,
    // Só para OpenObserve local (http://localhost:5080); em test/prod fica HTTPS.
    insecureHTTP: env.VITE_OO_INSECURE_HTTP === 'true',
    service: settings.SERVICE_NAME,
    env: settings.ENV,
    version: settings.VERSION,
  }

  openobserveRum.init({
    ...common,
    // `income_web_<env>`: separa dev/test/prod (o `env` do RUM só existe em `o2tags`).
    applicationId: settings.APPLICATION_ID,
    // Injeta traceparent/tracestate/baggage na API: o CORS do back precisa liberá-los.
    allowedTracingUrls: [settings.endpoint],
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: PRIVACY_LEVEL,
    // Session Replay desligado (0) salvo se `VITE_OO_REPLAY_SAMPLE_RATE` for definido.
    sessionReplaySampleRate: REPLAY_SAMPLE_RATE,
    beforeSend,
  })

  openobserveLogs.init({
    ...common,
    // Os logs vão pelo sink do logger; erros não capturados já viram eventos do RUM.
    forwardErrorsToLogs: false,
  })

  // Nomes iguais aos do back (recurso do OTel) para filtrar front e back pelo mesmo campo.
  // `service`, `version` e `env` do SDK continuam: as telas de RUM do OpenObserve os usam.
  openobserveLogs.setGlobalContext({
    service_name: settings.SERVICE_NAME,
    service_namespace: 'income',
    service_version: settings.VERSION,
    deployment_environment_name: settings.ENV,
  })

  registerSink(({ level, args, context }) => {
    const [primary, ...rest] = args
    let message
    const attributes = scrub(context)
    attributes.severity = SEVERITY[level]

    if (primary instanceof Error) {
      message = redactString(primary.message)
      attributes.error = { ...normalizeError(primary), stack: redactString(primary.stack) }
    } else {
      message = typeof primary === 'string' ? redactString(primary) : REDACTED
    }
    if (rest.length) attributes.extra = scrub(rest)

    openobserveLogs.logger[level]?.(message, attributes)
  })

  // Mesmo `minimal_id` que o back grava no span como usr_id/usr_link_id.
  const authStore = useAuthStore()
  watch(
    () => {
      const { user, link } = authStore.auth_data ?? {}
      // Chaves iguais às do back no span (`usr_*`): `usr.link_id` vira `usr_link_id` no OpenObserve.
      return [
        user?.minimal_id,
        link?.minimal_id,
        user?.email,
        user?.username,
        link?.name,
        link?.document,
      ]
    },
    ([userId, linkId, email, username, linkName, linkDocument]) => {
      // RUM e Logs são SDKs separados: cada um guarda o seu próprio `usr`.
      if (userId || linkId) {
        // `email` é o que a tela de Sessions/replay do OpenObserve exibe como usuário.
        const user = {
          id: userId,
          link_id: linkId,
          email,
          username,
          link_name: linkName,
          link_document: linkDocument,
        }
        openobserveRum.setUser(user)
        openobserveLogs.setUser(user)
      } else {
        openobserveRum.clearUser()
        openobserveLogs.clearUser()
      }
    },
    { immediate: true },
  )
}
